import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/format";
import { Badge } from "@/components/Badge";
import { CONTENT_STATUS, TASK_STATUS, PRIORITY, CONTENT_TYPES, JOB_ROLE_LABEL, label } from "@/lib/meta";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Play, CheckCircle2, ThumbsUp, Send, ListTodo, ChevronRight, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABEL = { task: "Task", content: "Content", ...Object.fromEntries(CONTENT_TYPES.map((t) => [t.v, t.l])) };

const SCOPES = [
  { v: "today", l: "Today" },
  { v: "overdue", l: "Overdue" },
  { v: "week", l: "This Week" },
  { v: "all", l: "All" },
];

function StatusBadge({ r }) {
  if (r.entity_type === "task") return <Badge color={TASK_STATUS[r.status]?.color}>{label(TASK_STATUS, r.status)}</Badge>;
  return <Badge color={CONTENT_STATUS[r.status]?.color}>{label(CONTENT_STATUS, r.status)}</Badge>;
}

export function PendingWorkTable({ types = null }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data, isLoading } = useQuery({ queryKey: ["pending-work"], queryFn: async () => (await api.get("/pending-work")).data });

  const [client, setClient] = useState("all");
  const [scope, setScope] = useState("week");
  const [priority, setPriority] = useState("all");
  const [wtype, setWtype] = useState("all");
  const [status, setStatus] = useState("all");
  const [emp, setEmp] = useState("all");
  const [roleF, setRoleF] = useState("all");

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["pending-work"] });
    qc.invalidateQueries({ queryKey: ["my-day"] });
    ["ws-my-clients", "pool-design", "pool-video", "dashboard"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  };

  const act = async (r, next, msg) => {
    const url = r.entity_type === "task" ? `/tasks/${r.id}` : `/content/${r.id}`;
    await api.put(url, { status: next });
    toast.success(msg);
    refresh();
  };

  const base = useMemo(() => (data ? (types ? data.rows.filter((r) => types.includes(r.work_type)) : data.rows) : []), [data, types]);
  const baseClients = useMemo(() => {
    const seen = new Map();
    base.forEach((r) => { if (r.client_id && !seen.has(r.client_id)) seen.set(r.client_id, r.client_name); });
    return [...seen.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [base]);

  const rows = useMemo(() => {
    if (!data) return [];
    return base.filter((r) => {
      if (client !== "all" && r.client_id !== client) return false;
      if (priority !== "all" && r.priority !== priority) return false;
      if (wtype !== "all" && r.work_type !== wtype) return false;
      if (status !== "all" && r.status !== status) return false;
      if (emp !== "all" && r.owner_id !== emp) return false;
      if (roleF !== "all" && r.owner_job_role !== roleF) return false;
      // active work (in progress / awaiting approval) is ALWAYS visible regardless of date scope
      const active = r.raw_status === "in_progress" || r.raw_status === "ready_for_approval";
      if (!active) {
        if (scope === "today" && !(r.is_today || r.overdue)) return false;
        if (scope === "overdue" && !r.overdue) return false;
        if (scope === "week" && !r.is_week) return false;
      }
      return true;
    }).sort((a, b) => {
      const pr = { high: 0, medium: 1, low: 2 };
      if (a.overdue !== b.overdue) return a.overdue ? -1 : 1;
      if (pr[a.priority] !== pr[b.priority]) return pr[a.priority] - pr[b.priority];
      return (a.date || "").localeCompare(b.date || "");
    });
  }, [data, base, client, scope, priority, wtype, status, emp, roleF]);

  if (isLoading || !data)
    return <Card className="flex h-40 items-center justify-center rounded-lg border-zinc-200"><Loader2 className="h-5 w-5 animate-spin text-zinc-400" /></Card>;

  const isManager = data.is_manager;
  const isBoss = user?.role === "super_admin" || user?.role === "manager";
  const uid = user?.id;
  const canComplete = (r) => isBoss || (r.entity_type === "task" ? r.owner_id === uid : r.started_by_id === uid);
  const canUndo = (r) => {
    if (isBoss) return true;
    if (r.entity_type === "task") return r.raw_status === "in_progress" && r.owner_id === uid;
    if (r.raw_status === "in_progress") return r.started_by_id === uid;
    if (r.raw_status === "ready_for_approval") return r.completed_by_id === uid;
    return false;
  };

  const revert = async (r) => {
    const back = r.entity_type === "task"
      ? "todo"
      : { in_progress: "planned", ready_for_approval: "in_progress", approved: "ready_for_approval", posted: "approved" }[r.raw_status];
    if (!back) return;
    await act(r, back, "Reverted a step");
  };
  const canRevert = (r) => r.entity_type === "task"
    ? r.raw_status === "in_progress"
    : ["in_progress", "ready_for_approval", "approved", "posted"].includes(r.raw_status);

  const mutedInProgress = (r) => <span className="text-xs italic text-zinc-400" data-testid={`pw-locked-${r.id}`}>In progress{r.owner ? ` · ${r.owner}` : ""}</span>;

  const actionFor = (r) => {
    if (r.entity_type === "task") {
      if (r.raw_status === "in_progress") return canComplete(r)
        ? <Button size="sm" onClick={() => act(r, "completed", "Task completed")} data-testid={`pw-action-${r.id}`}><CheckCircle2 className="mr-1 h-3 w-3" />Complete</Button>
        : mutedInProgress(r);
      return <Button size="sm" variant="outline" onClick={() => act(r, "in_progress", "Task started")} data-testid={`pw-action-${r.id}`}><Play className="mr-1 h-3 w-3" />Start</Button>;
    }
    switch (r.raw_status) {
      case "planned": return <Button size="sm" onClick={() => act(r, "in_progress", "Started")} data-testid={`pw-action-${r.id}`}><Play className="mr-1 h-3 w-3" />Start Work</Button>;
      case "in_progress": return canComplete(r)
        ? <Button size="sm" variant="outline" onClick={() => act(r, "ready_for_approval", "Marked completed")} data-testid={`pw-action-${r.id}`}><CheckCircle2 className="mr-1 h-3 w-3" />Mark Completed</Button>
        : mutedInProgress(r);
      case "ready_for_approval": return <Button size="sm" variant="outline" onClick={() => act(r, "approved", "Approved")} data-testid={`pw-action-${r.id}`}><ThumbsUp className="mr-1 h-3 w-3" />Approve</Button>;
      case "approved": return <Button size="sm" onClick={() => act(r, "posted", "Posted")} data-testid={`pw-action-${r.id}`}><Send className="mr-1 h-3 w-3" />Mark Posted</Button>;
      default: return null;
    }
  };

  return (
    <Card className="overflow-hidden rounded-lg border-zinc-200 shadow-sm" data-testid="pending-work-table">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-zinc-100 px-5 py-4">
        <div className="flex items-center gap-2">
          <ListTodo className="h-4 w-4 text-zinc-500" />
          <h3 className="font-heading text-base font-semibold">{isManager ? "Today's Pending Work — Agency" : "Today's Pending Work"}</h3>
          <Badge color="orange" data-testid="pw-count">{rows.length}</Badge>
        </div>
      </div>

      {/* client chips */}
      <div className="flex flex-wrap gap-1.5 border-b border-zinc-100 px-5 py-3">
        <button onClick={() => setClient("all")} data-testid="pw-client-all" className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${client === "all" ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}>All Clients</button>
        {baseClients.map((c) => (
          <button key={c.id} onClick={() => setClient(c.id)} data-testid={`pw-client-${c.id}`} className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${client === c.id ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}>{c.name}</button>
        ))}
      </div>

      {/* filters */}
      <div className="flex flex-wrap items-center gap-2 border-b border-zinc-100 bg-zinc-50/60 px-5 py-3">
        <div className="flex rounded-md border border-zinc-200 bg-white p-0.5">
          {SCOPES.map((s) => (
            <button key={s.v} onClick={() => setScope(s.v)} data-testid={`pw-scope-${s.v}`} className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${scope === s.v ? "bg-zinc-900 text-white" : "text-zinc-500 hover:text-zinc-900"}`}>{s.l}</button>
          ))}
        </div>
        <Select value={priority} onValueChange={setPriority}><SelectTrigger className="h-8 w-32 bg-white text-xs" data-testid="pw-priority"><SelectValue placeholder="Priority" /></SelectTrigger><SelectContent><SelectItem value="all">All Priority</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="low">Low</SelectItem></SelectContent></Select>
        <Select value={wtype} onValueChange={setWtype}><SelectTrigger className="h-8 w-36 bg-white text-xs" data-testid="pw-worktype"><SelectValue placeholder="Work Type" /></SelectTrigger><SelectContent><SelectItem value="all">All Work Types</SelectItem>{[...new Set(base.map((r) => r.work_type))].map((w) => <SelectItem key={w} value={w}>{TYPE_LABEL[w] || w}</SelectItem>)}</SelectContent></Select>
        <Select value={status} onValueChange={setStatus}><SelectTrigger className="h-8 w-36 bg-white text-xs" data-testid="pw-status"><SelectValue placeholder="Status" /></SelectTrigger><SelectContent><SelectItem value="all">All Statuses</SelectItem>{[...new Set(base.map((r) => r.status))].map((s) => <SelectItem key={s} value={s}>{CONTENT_STATUS[s]?.label || TASK_STATUS[s]?.label || s}</SelectItem>)}</SelectContent></Select>
        {isManager && <Select value={emp} onValueChange={setEmp}><SelectTrigger className="h-8 w-40 bg-white text-xs" data-testid="pw-employee"><SelectValue placeholder="Employee" /></SelectTrigger><SelectContent><SelectItem value="all">All Employees</SelectItem>{data.employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent></Select>}
        {isManager && <Select value={roleF} onValueChange={setRoleF}><SelectTrigger className="h-8 w-40 bg-white text-xs" data-testid="pw-role"><SelectValue placeholder="Role" /></SelectTrigger><SelectContent><SelectItem value="all">All Roles</SelectItem>{Object.entries(JOB_ROLE_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select>}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-zinc-100 bg-white text-xs uppercase tracking-wide text-zinc-400">
            <th className="px-4 py-2.5 text-left font-medium">Client</th>
            <th className="px-4 py-2.5 text-left font-medium">Work</th>
            <th className="px-4 py-2.5 text-left font-medium">Type</th>
            <th className="px-4 py-2.5 text-left font-medium">Planned</th>
            <th className="px-4 py-2.5 text-left font-medium">Priority</th>
            <th className="px-4 py-2.5 text-left font-medium">Status</th>
            {isManager && <th className="px-4 py-2.5 text-left font-medium">Owner</th>}
            <th className="px-4 py-2.5 text-right font-medium">Action</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={isManager ? 8 : 7} className="py-12 text-center text-sm text-zinc-400">Nothing pending here 🎉 — you're all caught up.</td></tr>}
            {rows.map((r) => (
              <tr key={`${r.entity_type}-${r.id}`} className="border-b border-zinc-50 hover:bg-zinc-50" data-testid={`pw-row-${r.id}`}>
                <td className="px-4 py-2.5 font-medium text-zinc-700">{r.client_name}</td>
                <td className="px-4 py-2.5">
                  <button onClick={() => r.client_id && navigate(`/clients/${r.client_id}`)} className="text-left hover:underline">{r.title}</button>
                </td>
                <td className="px-4 py-2.5 capitalize text-zinc-500">{TYPE_LABEL[r.work_type] || r.work_type}</td>
                <td className="px-4 py-2.5">
                  {r.date ? <span className={r.overdue ? "font-medium text-red-600" : r.is_today ? "font-medium text-amber-600" : "text-zinc-500"}>{r.overdue ? "Overdue · " : r.is_today ? "Today · " : ""}{fmtDate(r.date)}</span> : "—"}
                </td>
                <td className="px-4 py-2.5"><Badge color={PRIORITY[r.priority]?.color}>{label(PRIORITY, r.priority)}</Badge></td>
                <td className="px-4 py-2.5"><StatusBadge r={r} /></td>
                {isManager && <td className="px-4 py-2.5 text-zinc-500">{r.owner || <span className="text-zinc-300">Unassigned</span>}</td>}
                <td className="px-4 py-2.5">
                  <div className="flex items-center justify-end gap-1.5">
                    {canRevert(r) && canUndo(r) && <Button size="sm" variant="ghost" className="px-2 text-zinc-400 hover:text-zinc-700" onClick={() => revert(r)} title="Undo last step" data-testid={`pw-undo-${r.id}`}><RotateCcw className="h-3.5 w-3.5" /></Button>}
                    {actionFor(r)}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
